import React, { ReactNode } from 'react'
import { motion, useCycle } from 'framer-motion'

interface AnimateButtonProps {
  children: ReactNode
  type?: 'slide' | 'scale' | 'rotate'
  direction?: 'up' | 'down' | 'left' | 'right'
  offset?: number
  scale?: number | { hover: number; tap: number }
}

const AnimateButton = React.forwardRef<HTMLDivElement, AnimateButtonProps>(
  (
    { children, type = 'scale', direction = 'right', offset = 10, scale = { hover: 1, tap: 0.9 } },
    ref
  ) => {
    const isVertical = direction === 'up' || direction === 'down'
    const offset1 = direction === 'up' || direction === 'left' ? offset : 0
    const offset2 = direction === 'up' || direction === 'left' ? 0 : offset

    const [x, cycleX] = useCycle(offset1, offset2)
    const [y, cycleY] = useCycle(offset1, offset2)

    switch (type) {
      case 'rotate': {
        return (
          <motion.div
            animate={{ rotate: 360 }}
            ref={ref}
            transition={{
              repeat: Infinity,
              repeatType: 'loop',
              duration: 2,
              repeatDelay: 0,
            }}
          >
            {children}
          </motion.div>
        )
      }
      case 'slide': {
        return (
          <motion.div
            animate={isVertical ? { y: y !== undefined ? y : '' } : { x: x !== undefined ? x : '' }}
            onHoverEnd={isVertical ? () => cycleY() : () => cycleX()}
            onHoverStart={isVertical ? () => cycleY() : () => cycleX()}
            ref={ref}
          >
            {children}
          </motion.div>
        )
      }
      case 'scale':
      default: {
        const scaleValue = typeof scale === 'number' ? { hover: scale, tap: scale } : scale
        return (
          <motion.div
            ref={ref}
            whileHover={{ scale: scaleValue?.hover }}
            whileTap={{ scale: scaleValue?.tap }}
          >
            {children}
          </motion.div>
        )
      }
    }
  }
)

export default AnimateButton
